import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "../../../utils/translation";
import {
  listDigitalCards,
  selectDigitalCards,
  selectDigitalCardsError,
  selectDigitalCardsLoading,
  selectDigitalCardsMessage,
} from "../digitalCardsSlice";
import { listUsers, selectUsers } from "../../user/userSlice";
import digitalCardsService from "../digitalCardsService";

const PAGE_SIZE = 10;

const DigitalCardsListPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const cards = useSelector(selectDigitalCards);
  const isLoading = useSelector(selectDigitalCardsLoading);
  const isError = useSelector(selectDigitalCardsError);
  const message = useSelector(selectDigitalCardsMessage);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showDetail, setShowDetail] = useState(false);
  const [detailCard, setDetailCard] = useState(null);
  const [detailImage, setDetailImage] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    dispatch(listDigitalCards());
    dispatch(listUsers());
  }, [dispatch]);

  const users = useSelector(selectUsers);

  const userMap = useMemo(() => {
    const map = new Map();
    (users || []).forEach((u) => {
      const id = u.id || u.userId;
      if (!id) return;
      const fullName = u.fullName || [u.name, u.surName].filter(Boolean).join(" ") || "";
      map.set(String(id), { name: fullName || "-", email: u.email || u.userEmail || "-" });
    });
    return map;
  }, [users]);

  const getOwnerId = (card) => {
    return (
      card?.userId ||
      card?.activateUserId ||
      (Array.isArray(card?.adminUsers) ? card.adminUsers[0] : undefined)
    );
  };

  const getImageSrc = (raw) => {
    if (!raw) return "";
    if (raw.startsWith("data:")) return raw;
    return `data:image/jpeg;base64,${raw}`;
  };

  const getName = (card) => {
    const ownerId = getOwnerId(card);
    if (ownerId && userMap.has(String(ownerId))) return userMap.get(String(ownerId)).name;
    return card?.fullName || card?.name || "-";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cards || [];
    return (cards || []).filter((c) => getName(c).toLowerCase().includes(term));
  }, [cards, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const formatDate = (value) => {
    if (!value) return "-";
    if (typeof value?.toDate === "function") return value.toDate().toLocaleString("tr-TR");
    if (typeof value === "number") return new Date(value).toLocaleString("tr-TR");
    if (typeof value === "string") return value;
    return "-";
  };

  const openDetail = async (card) => {
    setDetailCard(card);
    setShowDetail(true);
    setDetailLoading(true);
    try {
      const image = await digitalCardsService.getDigitalCardImage(card.id);
      setDetailImage(image || "");
    } finally {
      setDetailLoading(false);
    }
  };

  const renderRows = () => {
    if (isLoading && (!cards || cards.length === 0)) {
      return (
        <tr>
          <td colSpan="6">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!cards || cards.length === 0)) {
      return (
        <tr>
          <td colSpan="6" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!paged || paged.length === 0) {
      return (
        <tr>
          <td colSpan="6">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return paged.map((c) => (
      <tr
        key={c.id}
        style={{ cursor: "pointer" }}
        onClick={() => openDetail(c)}
      >
        <td>{c.id}</td>
        <td>{highlightMatch(getName(c), search.trim())}</td>
        <td>{(userMap.get(String(getOwnerId(c)))?.email) || c.email || "-"}</td>
        <td>{c.phone || "-"}</td>
        <td>{c.isActive ? t("admin_common.status_active") : t("admin_common.status_passive")}</td>
        <td>{formatDate(c.createdAt)}</td>
      </tr>
    ));
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card w-100" style={{ maxWidth: "1800px", margin: "0 auto" }}>
            <div className="card-body">
              <h4 className="card-title">{t("admin_digital_cards_pages.list_title")}</h4>
              <div className="mb-3 d-flex justify-content-end">
                <Link to="/digital-cards/create" className="btn btn-success">
                  Ekle
                </Link>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
            <div className="table-responsive">
              <table className="table table-striped" style={{ fontSize: "0.9rem" }}>
                  <thead>
                    <tr>
                    <th>{t("admin_common.id")}</th>
                    <th>{t("admin_common.name")}</th>
                    <th>{t("admin_common.email")}</th>
                    <th>{t("admin_common.phone")}</th>
                    <th>{t("admin_common.status")}</th>
                    <th>{t("admin_common.created_at")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">
                  {t("admin_common.total_records", { count: filtered.length })}
                </span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDetail && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("admin_digital_cards_pages.detail_title")}</h5>
                <button type="button" className="btn-close" onClick={() => setShowDetail(false)}>
                  <span></span>
                </button>
              </div>
            <div className="modal-body">
              {detailCard && (
                <div className="row mb-3">
                  <div className="col-md-6 mb-2"><strong>ID:</strong> {detailCard.id || "-"}</div>
                  <div className="col-md-6 mb-2"><strong>Ad:</strong> {getName(detailCard)}</div>
                  <div className="col-md-6 mb-2"><strong>E-posta:</strong> {(userMap.get(String(getOwnerId(detailCard)))?.email) || detailCard.email || "-"}</div>
                  <div className="col-md-6 mb-2"><strong>Telefon:</strong> {detailCard.phone || "-"}</div>
                  <div className="col-md-6 mb-2"><strong>Durum:</strong> {detailCard.isActive ? t("admin_common.status_active") : t("admin_common.status_passive")}</div>
                  <div className="col-md-6 mb-2"><strong>Oluşturulma:</strong> {formatDate(detailCard.createdAt)}</div>
                </div>
              )}
              {detailLoading ? (
                <div className="text-muted">{t("admin_common.loading")}</div>
              ) : detailImage ? (
                <img
                  src={getImageSrc(detailImage)}
                  alt="Kartvizit"
                  style={{ width: "100%", borderRadius: 8 }}
                />
              ) : (
                <div className="text-muted">{t("admin_digital_cards_pages.image_not_found")}</div>
              )}
            </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DigitalCardsListPage;
